import { SpriteFace } from '../core/sprite';
import {
  BALL_PAL,
  BALL_ROWS,
  DEMO_FACE,
  Grid8,
  ICON_ABC,
  ICON_PRISON,
  ICON_SIMPLE,
  PAL_ABC,
  PAL_PRISON,
  PAL_SIMPLE,
} from './docPix';

/** "What is this?" — the intro, ported from design/what-is-this.html. */
export function Intro({ onBack, onRules }: { onBack: () => void; onRules: () => void }) {
  return (
    <div className="doc">
      <div className="backbar">
        <button className="back" aria-label="Back" onClick={onBack}>
          ◀
        </button>
        <span className="r">WHAT IS THIS?</span>
      </div>

      <header className="hero">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
          <Grid8 rows={BALL_ROWS} palette={BALL_PAL} size={56} />
        </div>
        <h1>WHAT IS THIS?</h1>
        <p>The song league, rebuilt to look like something you'd have played in 1994.</p>
        <p style={{ marginTop: 12 }}>You're not meant to enjoy it. This place is a prison.</p>
      </header>

      <section>
        <h2>THE SHORT VERSION</h2>
        <p>
          Every week there's a theme. Everybody puts something in — usually a song, sometimes a picture or a video — and
          then pretends it wasn't them. <b>Nobody knows whose is whose.</b>
        </p>
        <p>
          You score the lot, guess who's responsible for what, and veto the one that's ruined your week. It all comes out
          at once on Friday.
        </p>
        <p>Three things get scored: how good your pick was, how badly you read everyone else, and how well you got away with it.</p>
      </section>

      <section>
        <h2>HOW A WEEK GOES</h2>
        <div className="steps">
          <div className="step">
            <div>
              <h3>YOU PUT ONE IN</h3>
              <p>
                One pick for the theme. You can also make a <b>call</b> — name the obvious one somebody's definitely going
                to submit. If they do, it pays, and you get to be insufferable about it.
              </p>
            </div>
          </div>
          <div className="step">
            <div>
              <h3>YOU VOTE</h3>
              <p>Spread your points about, name who's responsible for what, and veto the one that's ruined your week.</p>
            </div>
          </div>
          <div className="step">
            <div>
              <h3>IT ALL COMES OUT</h3>
              <p>Scores, names, who called it, who went down. Then it starts again.</p>
            </div>
          </div>
        </div>
        <p>Both windows shut early once everyone's done, so the week is exactly as long as the slowest of you.</p>
      </section>

      <section>
        <h2>PICK HOW IT LOOKS</h2>
        <p>
          Same game, same scores, three completely different front ends. Switch whenever. It changes nothing except what
          you're looking at while you do something spiteful.
        </p>
        <div className="styles">
          <div className="style st-p">
            <span className="em">
              <Grid8 rows={ICON_PRISON} palette={PAL_PRISON} size={40} />
            </span>
            <span>
              <span className="nm">THIS PLACE IS A PRISON</span>
              <span className="ds">Walk the wing. Cells, the yard, the mess. Somebody is going in the hole.</span>
            </span>
          </div>
          <div className="style st-a">
            <span className="em">
              <Grid8 rows={ICON_ABC} palette={PAL_ABC} size={40} />
            </span>
            <span>
              <span className="nm">THIS PLACE IS A MEMORY</span>
              <span className="ds">The ABC on Sauchiehall Street. Coat in at the balcony, then Polar Bar, the main floor and down to ABC2. Gone now.</span>
            </span>
          </div>
          <div className="style st-s">
            <span className="em">
              <Grid8 rows={ICON_SIMPLE} palette={PAL_SIMPLE} size={40} />
            </span>
            <span>
              <span className="nm">THIS PLACE IS SIMPLE</span>
              <span className="ds">No map, no walking about. A list of what you owe and nothing else.</span>
            </span>
          </div>
        </div>
        <div className="box">
          <div className="lab">IF YOU CAN'T BE BOTHERED WITH ANY OF IT</div>
          <p>
            Use <b>Simple</b>. One screen, everything a tap away, vote on the bus in thirty seconds. Nobody will judge you
            for it. They'll judge you for plenty of other things.
          </p>
        </div>
      </section>

      <section>
        <h2>YOU</h2>
        <p>
          A face, a number and a line about yourself. All of it yours to change whenever, under <b>About me</b>. Nobody is
          checking any of it.
        </p>
        <div className="you">
          <SpriteFace rows={DEMO_FACE} size={60} />
          <div className="lines">
            <div>
              <b>YOUR FACE</b>Draw it yourself. Twelve pixels square, four colours. Only the outline really shows at small
              sizes, so give yourself a hat or some glasses.
            </div>
            <div>
              <b>YOUR LINE</b>A sentence about your taste. Be honest or don't.
            </div>
            <div>
              <b>THINGS YOU SAY</b>Up to five. One comes out at random when somebody walks into you. Make them count.
            </div>
          </div>
        </div>
      </section>

      <section>
        <h2>NOT EVERY ROUND IS MUSIC</h2>
        <p>
          Some weeks it's pictures, some weeks it's videos. The symbol next to the theme tells you which.{' '}
          <b>It all scores exactly the same</b>, and you can be just as wrong in any of them.
        </p>
        <table>
          <tbody>
            <tr>
              <th>Round</th>
              <th>What you put in</th>
            </tr>
            <tr>
              <td>
                <b>Song</b>
              </td>
              <td>Search Spotify, pick one</td>
            </tr>
            <tr>
              <td>
                <b>Image</b>
              </td>
              <td>Upload a picture</td>
            </tr>
            <tr>
              <td>
                <b>Video</b>
              </td>
              <td>Paste a YouTube link</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>THE ACTUAL RULES</h2>
        <p>
          A few things catch everyone out the first time. What it costs to mark something down. How many of us have to
          agree before a veto does anything. What happens when two of you have the same idea.
        </p>
        <p>All in one place, with the numbers. Read it before you complain.</p>
        <p style={{ marginTop: 18 }}>
          <button
            onClick={onRules}
            style={{
              fontFamily: 'var(--display)',
              fontSize: 11,
              color: 'var(--k0)',
              background: 'transparent',
              border: '4px solid var(--k0)',
              padding: '14px 18px',
              cursor: 'pointer',
            }}
          >
            READ THE RULEBOOK
          </button>
        </p>
      </section>

      <div className="cta">
        <h2>GETTING IN</h2>
        <p>Sign in with Google or Microsoft. You'll be asked for a code once, then pick yourself off the list. Pick the right one.</p>
        <div className="code">BIDET</div>
      </div>
    </div>
  );
}
